/* ============================================================
   DBT: Shopify Inbox launcher sizing

   Scale only the CLOSED launcher:
   - desktop: 0.65
   - mobile: 0.80

   The scale is removed immediately before Inbox opens so the open chat
   window and its close button keep Shopify's native size.
   ============================================================ */

(() => {
  const DESKTOP_SCALE = 0.65;
  const MOBILE_SCALE = 0.8;
  const MOBILE_QUERY = '(max-width: 749px)';
  const HOST_ID = 'ShopifyChat';
  const SCALED_ATTRIBUTE = 'data-dbt-inbox-launcher-scaled';
  const BOUND_ATTRIBUTE = 'data-dbt-inbox-launcher-bound';

  let shadowObserver = null;
  let documentObserver = null;
  let scheduled = false;

  const getScale = () =>
    window.matchMedia(MOBILE_QUERY).matches ? MOBILE_SCALE : DESKTOP_SCALE;

  const isVisible = (element) => {
    if (!(element instanceof Element)) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      element.getAttribute('aria-hidden') !== 'true' &&
      rect.width > 0 &&
      rect.height > 0
    );
  };

  const getControlText = (element) =>
    [
      element.getAttribute('aria-label'),
      element.getAttribute('title'),
      element.textContent,
    ]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toLowerCase();

  const isCloseControl = (element) =>
    /close|minimi[sz]e|dismiss|hide chat/.test(getControlText(element));

  const clearScale = (element) => {
    if (!(element instanceof HTMLElement)) return;
    element.style.removeProperty('scale');
    element.style.removeProperty('transform-origin');
    element.removeAttribute(SCALED_ATTRIBUTE);
  };

  const clearScaledLaunchers = (root) => {
    root.querySelectorAll(`[${SCALED_ATTRIBUTE}]`).forEach(clearScale);
  };

  const isInboxOpen = (root) => {
    const expanded = root.querySelector('[aria-expanded="true"]');
    const visibleDialog = Array.from(
      root.querySelectorAll(
        '[role="dialog"], [class*="chat-window" i], [class*="chat-panel" i], [class*="conversation" i]'
      )
    ).some(isVisible);
    const visibleClose = Array.from(root.querySelectorAll('button')).some(
      (button) => isVisible(button) && isCloseControl(button)
    );

    return Boolean(expanded || visibleDialog || visibleClose);
  };

  const findClosedLauncher = (root) => {
    const preferred = root.querySelectorAll(
      'button.chat-toggle, button[class*="chat-toggle" i], .chat-app > button, button[aria-haspopup="dialog"]'
    );

    return Array.from(preferred).find((button) => {
      if (!isVisible(button)) return false;
      if (button.getAttribute('aria-expanded') === 'true') return false;
      return !isCloseControl(button);
    });
  };

  const bindOpenProtection = (launcher) => {
    if (!(launcher instanceof HTMLElement) || launcher.hasAttribute(BOUND_ATTRIBUTE)) return;

    launcher.setAttribute(BOUND_ATTRIBUTE, 'true');

    const restoreNativeOpenSize = () => {
      /* Clear before Shopify changes the launcher into its open/close state. */
      clearScale(launcher);
      window.requestAnimationFrame(scheduleApply);
      window.setTimeout(scheduleApply, 80);
      window.setTimeout(scheduleApply, 250);
    };

    launcher.addEventListener('pointerdown', () => clearScale(launcher), true);
    launcher.addEventListener('click', restoreNativeOpenSize, true);
    launcher.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter' || event.key === ' ') restoreNativeOpenSize();
      },
      true
    );
  };

  const applyToShadowInbox = (host) => {
    const root = host.shadowRoot;
    if (!root) return false;

    if (isInboxOpen(root)) {
      clearScaledLaunchers(root);
    } else {
      const launcher = findClosedLauncher(root);
      clearScaledLaunchers(root);

      if (launcher instanceof HTMLElement) {
        bindOpenProtection(launcher);
        const scale = String(getScale());

        if (launcher.style.getPropertyValue('scale') !== scale) {
          launcher.style.setProperty('scale', scale, 'important');
          launcher.style.setProperty('transform-origin', 'right bottom', 'important');
        }
        launcher.setAttribute(SCALED_ATTRIBUTE, 'true');
      }
    }

    if (!shadowObserver) {
      shadowObserver = new MutationObserver(scheduleApply);
      shadowObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        /* Do not observe style: our own scale styles must not create a loop. */
        attributeFilter: ['class', 'aria-expanded', 'aria-label', 'title', 'aria-hidden'],
      });
    }

    return true;
  };

  const applyLegacyFallback = () => {
    const iframe = document.getElementById('dummy-chat-button-iframe');
    if (!(iframe instanceof HTMLElement)) return false;

    iframe.style.setProperty('scale', String(getScale()), 'important');
    iframe.style.setProperty('transform-origin', 'right bottom', 'important');
    return true;
  };

  function apply() {
    scheduled = false;
    const host = document.getElementById(HOST_ID);

    if (host && applyToShadowInbox(host)) {
      if (documentObserver) {
        documentObserver.disconnect();
        documentObserver = null;
      }
      return;
    }

    applyLegacyFallback();
  }

  function scheduleApply() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(apply);
  }

  const start = () => {
    scheduleApply();

    documentObserver = new MutationObserver(scheduleApply);
    documentObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    let attempts = 0;
    const retryTimer = window.setInterval(() => {
      attempts += 1;
      scheduleApply();
      if (attempts >= 40) window.clearInterval(retryTimer);
    }, 250);

    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', scheduleApply);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(scheduleApply);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
